import {
  invalidateWorkflowExecution,
  resetWorkflowToStep,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  Icons,
  LoadingIndicator,
  useToggle,
} from '@commenda/kakashi';
import {
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { Dispatch, SetStateAction } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { useServiceRequestViewerCapabilities } from '../../../context/ServiceRequestViewerCapabilities';

const ConfirmationModal = ({
  isOpen,
  setIsOpen,
  onConfirm,
  config,
  isActionLoading = false,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onConfirm: () => void;
  config: {
    icon: React.ReactNode;
    heading: string;
    subText: React.ReactNode;
    actionButtonText?: string;
  };
  isActionLoading?: boolean;
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="p-6 flex flex-col gap-4">
        <div className="w-12 h-12 rounded-md bg-bg-warning flex justify-center items-center">
          {config.icon}
        </div>
        <div>
          <h3 className="txt-lg-bold text-text-primary-900 mb-2">
            {config.heading}
          </h3>
          <p className="txt-small-regular text-text-tertiary">
            {config.subText}
          </p>
        </div>
        <div className="flex justify-end gap-2 items-center mt-4">
          <Button onClick={onConfirm} size="md" disabled={isActionLoading}>
            {isActionLoading ? <LoadingIndicator size={16} /> : null}
            {config.actionButtonText || 'Confirm'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

type WorkflowResetToStepActionProps = {
  workflowExecutionId: string | undefined;
  targetStepDefinitionId: string | null | undefined;
  role: TaskParty;
  taskStatus: TaskStatus;
  onResetSuccess?: () => void;
};

// Reset is only meaningful on steps the workflow has actually reached — not on
// future / not-yet-active steps (which would jump the workflow forward).
const RESET_ELIGIBLE_STATUSES: TaskStatus[] = [
  TaskStatus.COMPLETED,
  TaskStatus.IN_PROGRESS,
];

export const WorkflowResetToStepAction = ({
  workflowExecutionId,
  targetStepDefinitionId,
  role,
  taskStatus,
  onResetSuccess,
}: WorkflowResetToStepActionProps) => {
  const { setNotification } = useNotification();
  const confirmModal = useToggle();
  const isResetting = useToggle();
  // False everywhere but the enterprise matter page for an affiliate admin.
  const { canManageRun } = useServiceRequestViewerCapabilities();

  const canReset =
    role === TaskParty.AGENT || role === TaskParty.ADMIN || canManageRun;

  if (
    !canReset ||
    !workflowExecutionId ||
    !targetStepDefinitionId ||
    !RESET_ELIGIBLE_STATUSES.includes(taskStatus)
  )
    return null;

  const handleReset = async () => {
    isResetting.on();
    const response = await resetWorkflowToStep({
      workflowExecutionId,
      targetStepDefinitionId,
    });

    if (!response?.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to reset workflow to this step',
      });
      isResetting.off();
      confirmModal.off();
      return;
    }

    // The backend re-syncs the service request from the runtime once the engine applies the
    // reset (via an engine callback) — we don't trigger the sync here. Give that a brief
    // moment to land, then refetch so the UI reflects the synced task statuses.
    await new Promise((resolve) => {
      setTimeout(resolve, 1200);
    });

    invalidateWorkflowExecution(workflowExecutionId);
    onResetSuccess?.();

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Workflow reset to this step',
    });
    isResetting.off();
    confirmModal.off();
  };

  return (
    <>
      <Button
        size="sm"
        variant="destructive-secondary"
        onClick={confirmModal.on}
      >
        Reset to this step
      </Button>
      <ConfirmationModal
        isOpen={confirmModal.visible}
        setIsOpen={confirmModal.setValue}
        onConfirm={handleReset}
        config={{
          icon: (
            <div className="w-12 h-12 rounded-md bg-bg-warning flex justify-center items-center">
              <Icons.AlertCircle className="text-utility-warning-700" />
            </div>
          ),
          heading: 'Reset workflow to this step?',
          subText:
            'This re-opens this step and discards progress on every step after it. Forms completed later will need to be submitted again — previously entered data is preserved and will pre-fill them. This action cannot be undone.',
          actionButtonText: 'Reset',
        }}
        isActionLoading={isResetting.value}
      />
    </>
  );
};
